/**
 * Vector Labs — Research API Layer
 *
 * Dual-mode: talks to the local Express server (port 3001) when
 * available, otherwise falls back to the in-browser localStorage store.
 */

import {
  getAllResearch,
  getPublishedResearch,
  getResearchById,
  getResearchByCategory,
  searchResearch,
  createResearch,
  updateResearch,
  deleteResearch,
  CATEGORIES,
} from '../data/researchStore';

const API_BASE = process.env.REACT_APP_API_URL || '';
const LATENCY = 120;

function delay(ms = LATENCY) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Check if the Express server is reachable */
let _serverAvailable = null;
async function serverAvailable() {
  if (_serverAvailable !== null) return _serverAvailable;
  try {
    const r = await fetch(`${API_BASE}/api/health`, { signal: AbortSignal.timeout(1200) });
    _serverAvailable = r.ok;
  } catch {
    _serverAvailable = false;
  }
  setTimeout(() => { _serverAvailable = null; }, 30000);
  return _serverAvailable;
}

function authHeaders() {
  const stored = localStorage.getItem('fvl_auth');
  if (stored) {
    try {
      const user = JSON.parse(stored);
      if (user.credential) return { Authorization: `Bearer ${user.credential}` };
      if (user.email) return { 'x-admin-email': user.email };
    } catch { /* ignore */ }
  }
  return {};
}

// ─── GET /api/research ──────────────────────────────────────────
export async function apiGetAllResearch({ status, category, q } = {}) {
  if (await serverAvailable()) {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (category) params.set('category', category);
    if (q) params.set('q', q);
    const r = await fetch(`${API_BASE}/api/research?${params}`);
    return r.json();
  }
  // Fallback to localStorage
  await delay();
  let results;
  if (q) {
    results = searchResearch(q);
  } else if (category) {
    results = getResearchByCategory(category);
  } else if (status === 'published') {
    results = getPublishedResearch();
  } else {
    results = getAllResearch();
  }
  return { ok: true, data: results, total: results.length };
}

// ─── GET /api/research/:id ──────────────────────────────────────
export async function apiGetResearchById(id) {
  if (await serverAvailable()) {
    const r = await fetch(`${API_BASE}/api/research/${id}`);
    return r.json();
  }
  await delay();
  const paper = getResearchById(id);
  if (!paper) return { ok: false, error: 'Not found', status: 404 };
  return { ok: true, data: paper };
}

// ─── POST /api/research ─────────────────────────────────────────
export async function apiCreateResearch(body) {
  if (await serverAvailable()) {
    const r = await fetch(`${API_BASE}/api/research`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(body),
    });
    return r.json();
  }
  await delay();
  if (!body.title || !body.authors || !body.abstract) {
    return { ok: false, error: 'Missing required fields: title, authors, abstract', status: 400 };
  }
  const paper = createResearch(body);
  return { ok: true, data: paper, status: 201 };
}

// ─── PUT /api/research/:id ──────────────────────────────────────
export async function apiUpdateResearch(id, body) {
  if (await serverAvailable()) {
    const r = await fetch(`${API_BASE}/api/research/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(body),
    });
    return r.json();
  }
  await delay();
  const paper = updateResearch(id, body);
  if (!paper) return { ok: false, error: 'Not found', status: 404 };
  return { ok: true, data: paper };
}

// ─── DELETE /api/research/:id ───────────────────────────────────
export async function apiDeleteResearch(id) {
  if (await serverAvailable()) {
    await fetch(`${API_BASE}/api/research/${id}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    return { ok: true };
  }
  await delay();
  deleteResearch(id);
  return { ok: true, status: 204 };
}

// ─── GET /api/categories ────────────────────────────────────────
export async function apiGetCategories() {
  if (await serverAvailable()) {
    const r = await fetch(`${API_BASE}/api/categories`);
    return r.json();
  }
  await delay();
  return { ok: true, data: CATEGORIES };
}

// ─── Upload file to S3 ─────────────────────────────────────────
export async function apiUploadFile(file) {
  const formData = new FormData();
  formData.append('file', file);
  const r = await fetch(`${API_BASE}/api/upload`, {
    method: 'POST',
    headers: authHeaders(),
    body: formData,
  });
  return r.json();
}

// ─── POST /api/research/reset ───────────────────────────────────
export async function apiResetResearch() {
  if (await serverAvailable()) {
    const r = await fetch(`${API_BASE}/api/research/reset`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
    });
    return r.json();
  }
  // Fallback to localStorage
  await delay();
  const { resetToSeed } = await import('../data/researchStore');
  const data = resetToSeed();
  return { ok: true, data, message: 'Research data has been reset.' };
}
